const INVALID_IP_MASK = '***';
const INVALID_IPV4_MASK = '***.***.***.***';

/**
 * Masks an IP address, keeping only the first and last segment visible.
 * IPv4: 123.45.67.89 -> 123.*.*.89
 * IPv6: 2001:db8::1 -> 2001:*:*:*:*:*:*:1
 * IPv4-mapped IPv6: ::ffff:192.0.2.128 -> ::ffff:192.*.*.128
 */
export function maskIpAddress(ip: string | null | undefined): string {
  if (!ip) {
    return '';
  }

  const trimmed = ip.trim();
  if (!trimmed) {
    return '';
  }

  if (isIpv4(trimmed)) {
    return maskIpv4(trimmed);
  }

  if (trimmed.includes('.')) {
    // IPv4-mapped IPv6 (e.g., ::ffff:192.0.2.128)
    const lastColonIndex = trimmed.lastIndexOf(':');
    if (lastColonIndex !== -1) {
      const prefix = trimmed.slice(0, lastColonIndex);
      const ipv4Part = trimmed.slice(lastColonIndex + 1);
      const maskedIpv4 = isIpv4(ipv4Part) ? maskIpv4(ipv4Part) : INVALID_IPV4_MASK;
      return `${prefix}:${maskedIpv4}`;
    }
  }

  const expanded = expandIpv6(trimmed);
  if (expanded) {
    return maskIpv6(expanded);
  }

  return INVALID_IP_MASK;
}

const IPV4_SEGMENT_REGEX = /^\d+$/;

function isIpv4(ip: string): boolean {
  const parts = ip.split('.');
  if (parts.length !== 4) {
    return false;
  }

  return parts.every((part) => {
    if (!IPV4_SEGMENT_REGEX.test(part)) {
      return false;
    }
    const value = Number(part);
    return value >= 0 && value <= 255;
  });
}

function maskIpv4(ip: string): string {
  const parts = ip.split('.');
  if (parts.length !== 4) {
    return INVALID_IPV4_MASK;
  }

  for (const part of parts) {
    if (!IPV4_SEGMENT_REGEX.test(part)) {
      return INVALID_IPV4_MASK;
    }
    const value = Number(part);
    if (value < 0 || value > 255) {
      return INVALID_IPV4_MASK;
    }
  }

  return `${parts[0]}.*.*.${parts[3]}`;
}

const IPV6_SEGMENT_REGEX = /^[0-9a-f]{1,4}$/i;

function expandIpv6(ip: string): string[] | null {
  let address = ip.toLowerCase();
  let ipv4Tail: string[] = [];

  if (address.includes('.')) {
    const lastColonIndex = address.lastIndexOf(':');
    if (lastColonIndex === -1) {
      return null;
    }

    const ipv4Part = address.slice(lastColonIndex + 1);
    const ipv4Bytes = toIpv4Bytes(ipv4Part);
    if (!ipv4Bytes) {
      return null;
    }

    ipv4Tail = convertIpv4BytesToHextets(ipv4Bytes);
    address = address.slice(0, lastColonIndex);

    if (address.endsWith(':')) {
      address = address.slice(0, -1);
    }
  }

  const doubleColonIndex = address.indexOf('::');
  if (doubleColonIndex !== -1) {
    if (address.indexOf('::', doubleColonIndex + 1) !== -1) {
      return null; // More than one '::' is invalid
    }

    const head = address.slice(0, doubleColonIndex);
    const tail = address.slice(doubleColonIndex + 2);

    const headParts = head ? head.split(':').filter(Boolean) : [];
    const tailParts = tail ? tail.split(':').filter(Boolean) : [];

    if (![...headParts, ...tailParts].every((part) => IPV6_SEGMENT_REGEX.test(part))) {
      return null;
    }

    const totalParts = headParts.length + tailParts.length + ipv4Tail.length;
    if (totalParts > 8) {
      return null;
    }

    const zerosToInsert = 8 - totalParts;
    const zeroSegments = Array(zerosToInsert).fill('0');
    const allParts = [...headParts, ...zeroSegments, ...tailParts, ...ipv4Tail];
    return allParts.map(normalizeIpv6Segment);
  }

  const parts = address ? address.split(':') : [];
  if (!parts.every((part) => part && IPV6_SEGMENT_REGEX.test(part))) {
    return null;
  }

  if (parts.length + ipv4Tail.length !== 8) {
    return null;
  }

  const allParts = [...parts, ...ipv4Tail];
  return allParts.map(normalizeIpv6Segment);
}

function normalizeIpv6Segment(segment: string): string {
  const clean = segment.toLowerCase();
  return clean.padStart(4, '0');
}

function maskIpv6(segments: string[]): string {
  if (segments.length === 0) {
    return INVALID_IP_MASK;
  }

  const displaySegments = segments.map((segment) => trimIpv6Segment(segment));

  return displaySegments
    .map((segment, index) => {
      if (index === 0 || index === displaySegments.length - 1) {
        return segment;
      }
      return '*';
    })
    .join(':');
}

function trimIpv6Segment(segment: string): string {
  const trimmed = segment.replace(/^0+/, '');
  return trimmed || '0';
}

function toIpv4Bytes(ipv4: string): number[] | null {
  const parts = ipv4.split('.');
  if (parts.length !== 4) {
    return null;
  }

  const bytes: number[] = [];
  for (const part of parts) {
    if (!IPV4_SEGMENT_REGEX.test(part)) {
      return null;
    }

    const value = Number(part);
    if (value < 0 || value > 255) {
      return null;
    }

    bytes.push(value);
  }

  return bytes;
}

function convertIpv4BytesToHextets(bytes: number[]): string[] {
  if (bytes.length !== 4) {
    return [];
  }

  const first = ((bytes[0] << 8) | bytes[1]).toString(16).padStart(4, '0');
  const second = ((bytes[2] << 8) | bytes[3]).toString(16).padStart(4, '0');

  return [first, second];
}
